import type { FieldPosition } from '../types/field-position';
import type { Field } from '../types/field';
import * as FieldHelper from './field.helper';

export function getPageHeight(documentPageId: string): number {
  const element = document.getElementById(documentPageId);
  if (!element) {
    return 0;
  }
  return element.offsetHeight;
}

export function checkPositionOccupied(
  fields: Field[],
  position: FieldPosition,
  pageId: string
): Field | undefined {
  return fields.find(
    (field: Field) =>
      field.position.left === position.left &&
      field.position.top === position.top &&
      field.pageId === pageId
  );
}

export function calculateFieldPlacementOnMultidrag(
  fields: Field[],
  oldPosition: FieldPosition,
  newPosition: FieldPosition
): Field[] {
  return fields.map((field: Field) => {
    const topDifference: number = newPosition.top - oldPosition.top;
    const leftDifference: number = newPosition.left - oldPosition.left;
    return {
      ...field,
      position: {
        ...field.position,
        top: field.position.top + topDifference,
        left: field.position.left + leftDifference,
      },
    };
  });
}

export function calculateFieldPositionForPlacement(
  position: FieldPosition,
  pageWidth: number
): FieldPosition {
  const ratio: number = FieldHelper.getPageRatio(pageWidth);
  return {
    top: Math.round(position.top * ratio),
    left: Math.round(position.left * ratio),
    height: Math.round(position.height * ratio),
    width: Math.round(position.width * ratio),
  };
}

export function calculateFieldPositionForSave(
  position: FieldPosition,
  pageWidth: number
): FieldPosition {
  const ratio: number = FieldHelper.getPageRatio(pageWidth);
  return {
    top: Math.round(position.top / ratio),
    left: Math.round(position.left / ratio),
    height: Math.round(position.height / ratio),
    width: Math.round(position.width / ratio),
  };
}

export function calculateFieldContainerPosition(fields: Field[]): FieldPosition {
  let top = 0;
  let left = 0;
  let right = 0;
  let bottom = 0;

  fields.forEach((field: Field, index) => {
    if (index === 0 || field.position.top < top) {
      ({ top } = field.position);
    }
    if (index === 0 || field.position.left < left) {
      ({ left } = field.position);
    }
    if (index === 0 || field.position.left + field.position.width > right) {
      right = field.position.left + field.position.width;
    }
    if (index === 0 || field.position.top + field.position.height > bottom) {
      bottom = field.position.top + field.position.height;
    }
  });

  return {
    top,
    left,
    width: right - left,
    height: bottom - top,
  };
}

export function calculatePosition(
  fields: Field[],
  newField: Field,
  pageWidth: number
): FieldPosition {
  const offsetPosition: FieldPosition = { ...newField.position };
  let positionOccupied = checkPositionOccupied(fields, offsetPosition, newField.pageId);
  while (positionOccupied) {
    offsetPosition.top += positionOccupied.position.height + 10;
    positionOccupied = checkPositionOccupied(fields, offsetPosition, newField.pageId);
  }
  return preventFieldPositionBeingOutsidePage(offsetPosition, newField.pageId, pageWidth);
}

export function preventFieldPositionBeingOutsidePage(
  fieldPosition: FieldPosition,
  documentPageId: string,
  pageWidth: number
) {
  const pageHeight: number = getPageHeight(documentPageId);
  const relativePosition: FieldPosition = calculateFieldPositionForPlacement(
    fieldPosition,
    pageWidth
  );
  const pixelsOutside: {
    right: number;
    bottom: number;
    left: number;
    top: number;
  } = calculatePixelsOutsidePage(relativePosition, pageWidth, pageHeight);

  if (pixelsOutside.right > 0) {
    relativePosition.left -= pixelsOutside.right;
  }
  if (pixelsOutside.bottom > 0) {
    relativePosition.top -= pixelsOutside.bottom;
  }
  if (pixelsOutside.left < 0) {
    relativePosition.left -= pixelsOutside.left;
  }
  if (pixelsOutside.top < 0) {
    relativePosition.top -= pixelsOutside.top;
  }
  return calculateFieldPositionForSave(relativePosition, pageWidth);
}

export function calculatePixelsOutsidePage(
  { top, left, width, height }: FieldPosition,
  pageWidth: number,
  pageHeight: number
): { right: number; bottom: number; left: number; top: number } {
  return {
    right: Math.ceil(left) + width - pageWidth,
    bottom: Math.ceil(top) + height - pageHeight,
    left: Math.ceil(left),
    top: Math.ceil(top),
  };
}
